import { oktaAuth } from './oktaInstance';
import { resetNamedUser } from '../Airship/AirshipUtilities';
import { callEvent } from '../Amplitude/AmplitudeUtilities';
import { getViteAppEnvironment } from '../Vite/getViteAppEnvironment';
import { isSessionTimeoutPromptActive } from './sessionTimeoutPrompt';

type SignOutOptions = {
  analytics?: {
    locationNum: string;
    sessionLength: string;
  };
  delayMs?: number;
  reason?: 'user-initiated' | 'token-expired' | 'token-missing';
};

export const SIGNOUT_IN_PROGRESS_KEY = 'signout-in-progress';

let signOutInProgress: Promise<void> | null = null;

const performFallbackSignOut = async (): Promise<void> => {
  try {
    await oktaAuth.tokenManager.clear();
  } catch (clearError) {
    console.error('Failed to clear token manager:', clearError);
  }
  localStorage.removeItem('okta-token-storage');

  try {
    await oktaAuth.signInWithRedirect();
  } catch {
    window.location.href = window.location.origin + '/';
  }
};

/**
 * Centralized sign-out utility that handles all logout scenarios:
 * - User-initiated logout (with analytics tracking)
 * - Automatic logout due to expired/invalid tokens
 * - Prevents race conditions with concurrent sign-out attempts
 *
 * @param options - Configuration for the sign-out behavior
 * @returns Promise that resolves when sign-out completes
 */
export const signOutUser = async (
  options: SignOutOptions = {}
): Promise<void> => {
  if (signOutInProgress) {
    return signOutInProgress;
  }

  // Persist across page reloads to prevent redirect loops
  if (sessionStorage.getItem(SIGNOUT_IN_PROGRESS_KEY)) {
    return;
  }

  const { analytics, delayMs, reason = 'user-initiated' } = options;

  // While the timeout prompt is up it owns the session decision, so a background
  // request must not evict the user mid-prompt. User-initiated sign-outs (the
  // modal's own idle logout included) are never suppressed. Checked before the
  // in-progress flag is set, so a suppressed attempt doesn't block a later one.
  if (reason !== 'user-initiated' && isSessionTimeoutPromptActive()) {
    return;
  }

  sessionStorage.setItem(SIGNOUT_IN_PROGRESS_KEY, 'true');

  signOutInProgress = (async () => {
    try {
      if (analytics) {
        try {
          callEvent('web logout', {
            locationNum: analytics.locationNum,
            sessionLength: analytics.sessionLength,
          });
        } catch (error) {
          console.error('Failed to track logout event:', error);
        }
      }

      if (getViteAppEnvironment() !== 'prod') {
        resetNamedUser().catch(error => {
          console.error('Failed to reset Airship named user:', error);
        });
      }

      // Handle delayed sign-out (e.g., for session timeout modal)
      if (delayMs) {
        setTimeout(async () => {
          try {
            await oktaAuth.signOut({
              postLogoutRedirectUri: window.location.origin + '/',
            });
          } catch (error) {
            console.error(
              `Failed to sign out from Okta (delayed, ${reason}):`,
              error
            );
            // Fallback: clear tokens and redirect manually
            await performFallbackSignOut();
          }
        }, delayMs);
        return;
      }

      // Immediate sign-out
      await oktaAuth.signOut({
        postLogoutRedirectUri: window.location.origin + '/',
      });
    } catch (error) {
      console.error(`Failed to sign out from Okta (${reason}):`, error);
      // Fallback: clear tokens and redirect manually
      await performFallbackSignOut();
    }
    // Cleared on the stored promise rather than in the async body: a delayed
    // sign-out returns before its first await, so a `finally` in the body would
    // run before the promise is assigned and leave it set for good.
  })().finally(() => {
    signOutInProgress = null;
  });

  return signOutInProgress;
};
